import { useEffect, useRef, useState } from "react";
import { getTips } from "../services/tipsService";
import type { TipTitle, TipType } from "../types/tips";

const PAGE_TITLE = "Tips & Tricks";

export interface TipsState {
  isLoading: boolean;
  allTips: TipType[];
  titles: TipTitle[];
  description: TipType[];
  typeId: string;
  descId: string;
  setTypeId: (value: string) => void;
  setDescId: (value: string) => void;
}

/**
 * Owns the Tips & Tricks data and the Type/Title filtering.
 *
 * The three effects mirror the original screen exactly (and intentionally each
 * re-fetch the same endpoint) to preserve behaviour. The string ids come
 * straight from `<select>` values, so comparisons normalise via `String(id)`
 * rather than the original loose `==`.
 *
 * Note: the original mutated the fetched objects in place (`item.title = res`);
 * here we push shallow copies instead — `allTips` only ever reads `id`/`type`,
 * so the rendered output is identical without the shared-reference mutation.
 */
export const useTips = (
  token: string | null,
  onLoaded: (status: boolean) => void
): TipsState => {
  const [isLoading, setIsLoading] = useState(true);
  const [allTips, setAllTips] = useState<TipType[]>([]);
  const [titles, setTitles] = useState<TipTitle[]>([]);
  const [description, setDescription] = useState<TipType[]>([]);
  const [typeId, setTypeId] = useState("0");
  const [descId, setDescId] = useState("0");

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  // Filter the visible cards by the selected Title (descId).
  useEffect(() => {
    if (!token) {
      return;
    }
    const run = async (): Promise<void> => {
      const { response: tips } = await getTips();
      const data: TipType[] = [];

      if (parseInt(descId, 10) > 0) {
        tips.forEach((item) => {
          if (String(item.id) === typeId) {
            const filteredTitles = item.title.filter(
              (element) => String(element.id) === descId
            );
            data.push({ ...item, title: filteredTitles });
          }
        });
      } else {
        tips.forEach((item) => {
          if (String(item.id) === typeId) {
            data.push({ ...item, title: [...item.title] });
          }
        });
      }

      setDescription(data);
      setAllTips(tips);
    };
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descId, token]);

  // Populate the Title dropdown for the selected Type (typeId).
  useEffect(() => {
    if (!token) {
      return;
    }
    const run = async (): Promise<void> => {
      const { response: tips } = await getTips();
      if (parseInt(typeId, 10) > 0) {
        const match = tips.find((item) => String(item.id) === typeId);
        setTitles(match ? match.title : []);
      } else {
        setTitles([]);
        setDescription(tips);
      }
      setAllTips(tips);
    };
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, token]);

  // Initial load.
  useEffect(() => {
    document.title = PAGE_TITLE;
    // Suppress the app-wide overlay; this screen renders its own skeleton.
    onLoadedRef.current(true);
    if (!token) {
      return;
    }
    const run = async (): Promise<void> => {
      try {
        const { response } = await getTips();
        setDescription(response);
        setAllTips(response);
      } catch (error) {
        console.warn("get Tips Error", error);
      } finally {
        setIsLoading(false);
      }
    };
    void run();
  }, [token]);

  return {
    isLoading,
    allTips,
    titles,
    description,
    typeId,
    descId,
    setTypeId,
    setDescId,
  };
};
